// Talks a driver through the Incident Details form one question at a time,
// out loud, instead of making them type it -- some drivers find it much
// easier to talk than type, especially one-handed at the roadside right
// after a crash. Each answer is transcribed and read by AI (Whisper +
// gpt-4o-mini, see transcribe-incident-report) to fill in the matching
// field(s); once the core questions are answered it does one audible pass
// checking for anything still missing ("I still need a couple of things...")
// before handing control back. The full back-and-forth is handed up as a
// transcript for Janelle to check later against what got filled in.
//
// Never the only way in: the ordinary typed form is still right there below
// this component at every step, and a driver can bail out of the voice flow
// at any point (mic denied, recording fails, or they just prefer typing).
// Nothing is auto-submitted -- the driver still reviews/edits the filled-in
// fields before moving on, same "AI drafts, a human checks" pattern as the
// rest of this app's AI features.
import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { speak } from "@/lib/speech";
import { Mic, Square, Loader2, AlertTriangle, Sparkles, Check, X as XIcon } from "lucide-react";

export interface VoiceIncidentFields {
  vehicle_number: string | null;
  location_description: string | null;
  weather_conditions: string | null;
  road_conditions: string | null;
  narrative: string | null;
  own_vehicle_damage_description: string | null;
  vehicle_location_now: string | null;
  police_involved: boolean;
  police_incident_number: string | null;
  witnesses_present: boolean;
  witness_details: string | null;
  injuries: boolean;
  injury_details: string | null;
  alcohol_drugs_involved: boolean;
  incident_date: string | null;
  incident_time: string | null;
}

interface VoiceIncidentRecorderProps {
  /** Vehicle numbers the driver could plausibly mean (for matching their spoken answer). */
  vehicleNumbers: string[];
  /** Whether the vehicle is already known (e.g. auto-filled from today's roster) -- skips that question. */
  vehicleAlreadyKnown: boolean;
  /** Called after each answer -- merge only the fields that answer actually covered. */
  onFieldsUpdated: (fields: Partial<VoiceIncidentFields>) => void;
  /** Called with the growing Q&A transcript, after every answer, so it's saved even if the driver stops partway through. */
  onTranscriptUpdated: (transcript: string) => void;
}

interface Question {
  key: string;
  prompt: string;
  /** Only asked if this returns true against the live form state. */
  shouldAsk?: () => boolean;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Matches a spoken vehicle number against the real fleet list -- Whisper
// usually renders "four one seven" as digits already, but this also handles
// stray punctuation/spacing and a partial match (e.g. heard "17" for "417").
// Deliberately returns null rather than the raw heard digits on anything
// that doesn't match a real vehicle -- confirmed live that Whisper can
// mishear a spoken number as a different-but-plausible one (heard "416" for
// a spoken "417"), and a wrong-but-confident vehicle number is worse than
// leaving the field blank for the driver to pick themselves.
function matchVehicleNumber(spoken: string | null, known: string[]): string | null {
  if (!spoken) return null;
  const digits = spoken.replace(/\D/g, "");
  if (!digits) return null;
  if (known.includes(digits)) return digits;
  return known.find((v) => v.endsWith(digits)) || null;
}

type Phase = "idle" | "recording" | "processing" | "gap_check" | "done" | "error";

const VoiceIncidentRecorder: React.FC<VoiceIncidentRecorderProps> = ({
  vehicleNumbers,
  vehicleAlreadyKnown,
  onFieldsUpdated,
  onTranscriptUpdated,
}) => {
  const questions: Question[] = [
    { key: "vehicle", prompt: "Which vehicle were you in?", shouldAsk: () => !vehicleAlreadyKnown },
    { key: "when", prompt: "What day was it, and about what time did this happen?" },
    { key: "where", prompt: "Where did this happen?" },
    {
      key: "what",
      prompt:
        "Now talk me through what happened. Also let me know about the weather, the road conditions, any damage to your taxi, and whether police, witnesses, injuries, or alcohol were involved.",
    },
  ];

  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [answeredSoFar, setAnsweredSoFar] = useState<{ key: string; summary: string }[]>([]);

  const transcriptRef = useRef("");
  // Which part of the flow the most recent recording belongs to, so
  // processAnswer knows whether to move to the next fixed question or
  // re-run the gap check -- and how many gap-check rounds have run, capped
  // so a driver who genuinely has nothing more to add is never stuck in a
  // loop of being asked the same thing.
  const modeRef = useRef<"questions" | "gap">("questions");
  const gapRoundRef = useRef(0);
  const MAX_GAP_ROUNDS = 2;
  // What the voice session itself has gathered so far -- tracked locally
  // rather than read back from the parent's form state, so the gap check
  // below never races against React's async state updates.
  const collectedRef = useRef<Partial<VoiceIncidentFields>>({});
  // The real source of truth for which question we're on. advance() is
  // called from deep inside a chain of callbacks (recorder.onstop ->
  // processAnswer -> advance) that all got wired up the instant the
  // previous question started recording -- long before the setQuestionIndex
  // from THAT render could ever cause a re-render. Reading the
  // `questionIndex` state from that closure meant every call in the chain
  // saw the index from whenever `begin()` first ran, forever -- confirmed
  // live: it kept recomputing "next question = when" and never advanced.
  // questionIndex (state) is kept alongside this purely so setQuestionIndex
  // calls don't need removing; this ref is what logic actually reads.
  const questionIndexRef = useRef(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const appendTranscript = (question: string, answer: string) => {
    transcriptRef.current += `${transcriptRef.current ? "\n\n" : ""}Q: ${question}\nA: ${answer || "(nothing heard)"}`;
    onTranscriptUpdated(transcriptRef.current);
  };

  const askQuestion = async (prompt: string) => {
    setCurrentPrompt(prompt);
    setError(null);
    await speak(prompt);
    void startRecording(prompt);
  };

  const startRecording = async (prompt: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => void processAnswer(prompt);
      mediaRecorderRef.current = recorder;
      recorder.start();
      setPhase("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      setError("Couldn't access the microphone. Check the browser has permission, or just fill the form in below.");
      setPhase("error");
    }
  };

  const stopRecording = () => {
    stopTimer();
    mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setPhase("processing");
  };

  const processAnswer = async (prompt: string) => {
    try {
      const mimeType = mediaRecorderRef.current?.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mimeType });
      if (blob.size < 2000) {
        setError("Didn't catch that -- try again, or fill the form in below.");
        setPhase("error");
        return;
      }
      const base64 = await blobToBase64(blob);
      const { data, error: fnError } = await supabase.functions.invoke("transcribe-incident-report", {
        body: { audio_base64: base64, mime_type: mimeType, now_local: new Date().toISOString(), question_hint: prompt },
      });
      if (fnError) throw fnError;
      if (!data?.fields) {
        appendTranscript(prompt, "");
        setError(data?.note || "Didn't catch that -- try again, or fill the form in below.");
        setPhase("error");
        return;
      }

      const transcript: string = data.transcript || "";
      appendTranscript(prompt, transcript);

      const fields = data.fields as VoiceIncidentFields;
      const matchedVehicle = matchVehicleNumber(fields.vehicle_number, vehicleNumbers);
      const merged = { ...fields, vehicle_number: matchedVehicle };
      collectedRef.current = {
        ...collectedRef.current,
        ...Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== null && v !== false)),
      };
      onFieldsUpdated(merged);
      setAnsweredSoFar((prev) => [...prev, { key: questions[questionIndexRef.current]?.key || "answer", summary: transcript.slice(0, 80) }]);

      if (modeRef.current === "questions") {
        await advance();
      } else {
        await runGapCheck();
      }
    } catch (e) {
      setError(`Couldn't process that (${e instanceof Error ? e.message : String(e)}) -- try again, or fill the form in below.`);
      setPhase("error");
    }
  };

  const advance = async () => {
    let nextIdx = questionIndexRef.current + 1;
    while (nextIdx < questions.length && questions[nextIdx].shouldAsk && !questions[nextIdx].shouldAsk!()) {
      nextIdx++;
    }
    if (nextIdx < questions.length) {
      questionIndexRef.current = nextIdx;
      setQuestionIndex(nextIdx);
      await askQuestion(questions[nextIdx].prompt);
    } else {
      modeRef.current = "gap";
      await runGapCheck();
    }
  };

  const runGapCheck = async () => {
    setPhase("gap_check");
    const current = collectedRef.current;
    const missingLabels: { field: keyof VoiceIncidentFields; label: string }[] = [
      { field: "weather_conditions", label: "the weather" },
      { field: "road_conditions", label: "the road conditions" },
      { field: "own_vehicle_damage_description", label: "any damage to your vehicle" },
      { field: "vehicle_location_now", label: "where your vehicle is now" },
    ];
    const missing = missingLabels.filter((m) => !(current[m.field] as string | null)?.trim?.());

    if (missing.length === 0 || gapRoundRef.current >= MAX_GAP_ROUNDS) {
      await speak(
        missing.length === 0
          ? "Great, that's everything I need. Have a look over the details below."
          : "That's okay, you can fill in anything else below."
      );
      setPhase("done");
      return;
    }

    gapRoundRef.current += 1;
    const list = missing.map((m) => m.label);
    const spoken =
      list.length === 1
        ? `I still need one more thing from you -- ${list[0]}.`
        : `I still need a few more things from you -- ${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}.`;
    const prompt = `Tell me about: ${list.join(", ")}.`;
    setCurrentPrompt(spoken);
    await speak(spoken);
    void startRecording(prompt);
  };

  const begin = async () => {
    setStarted(true);
    setAnsweredSoFar([]);
    transcriptRef.current = "";
    collectedRef.current = {};
    modeRef.current = "questions";
    gapRoundRef.current = 0;
    let firstIdx = 0;
    while (firstIdx < questions.length && questions[firstIdx].shouldAsk && !questions[firstIdx].shouldAsk!()) {
      firstIdx++;
    }
    questionIndexRef.current = firstIdx;
    setQuestionIndex(firstIdx);
    if (firstIdx < questions.length) {
      await askQuestion(questions[firstIdx].prompt);
    } else {
      modeRef.current = "gap";
      await runGapCheck();
    }
  };

  const retryCurrent = () => {
    setError(null);
    void startRecording(currentPrompt);
  };

  const mmss = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

  if (!started) {
    return (
      <div className="rounded-lg border border-yellow-700/40 bg-yellow-950/10 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-yellow-500 shrink-0" />
          <p className="text-sm font-medium text-yellow-500">Talk it through instead of typing</p>
        </div>
        <p className="text-xs text-muted-foreground">
          We'll ask you a few quick questions out loud and fill in the fields below from your answers -- you can still check and change anything before continuing.
        </p>
        <Button type="button" variant="outline" size="sm" onClick={begin}>
          <Mic className="w-4 h-4 mr-1.5" />
          Start
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-yellow-700/40 bg-yellow-950/10 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-yellow-500 shrink-0" />
        <p className="text-sm font-medium text-yellow-500">
          {phase === "done" ? "All done" : "Talking it through"}
        </p>
      </div>

      {answeredSoFar.length > 0 && phase !== "done" && (
        <div className="space-y-1">
          {answeredSoFar.map((a, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Check className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
              <span className="truncate">{a.summary}</span>
            </div>
          ))}
        </div>
      )}

      {(phase === "recording" || phase === "processing") && currentPrompt && (
        <p className="text-sm text-foreground italic">"{currentPrompt}"</p>
      )}

      {phase === "recording" && (
        <Button type="button" size="sm" onClick={stopRecording} className="bg-red-700 hover:bg-red-600 text-white">
          <Square className="w-3.5 h-3.5 mr-1.5" />
          Stop ({mmss})
        </Button>
      )}

      {phase === "processing" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Listening back...
        </div>
      )}

      {phase === "gap_check" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Checking what's still missing...
        </div>
      )}

      {phase === "done" && (
        <div className="flex items-start gap-1.5 text-sm text-green-500">
          <Check className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Check the fields below are right, then continue.</span>
        </div>
      )}

      {phase === "error" && error && (
        <div className="space-y-2">
          <div className="flex items-start gap-1.5 text-xs text-amber-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={retryCurrent}>
              <Mic className="w-4 h-4 mr-1.5" />
              Try Again
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setPhase("done")}>
              <XIcon className="w-4 h-4 mr-1.5" />
              I'll type the rest
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceIncidentRecorder;
